import React, { useEffect, useState } from "react";
import {
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useSelector, useDispatch } from "react-redux";
import { fetchCoupons, deleteCoupons } from "../../features/admin/CouponSlice";
import TableComponent from "../../components/TableComponent";
import { toast } from "react-toastify";
import CouponModal from "../../components/admin/CouponModal";
import { formatCurrencyVND, formatDate } from "../../utils/helps";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import ApiCustom from "../../utils/ApiCustom";
import StatusDropdown from "../../components/admin/StatusDropdownButton"; // Sử dụng đúng component

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (requiredPermission) => {
  if (userId == 1) {
    return true;
  }
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const statusOptions = [
  { value: "pending", label: "Chờ duyệt" },
  { value: "approved", label: "Đã duyệt" },
  { value: "denied", label: "Từ chối" },
];

const getStatusStyle = (value) => {
  switch (value) {
    case "approved":
      return "bg-green-100 text-green-800";
    case "denied":
      return "bg-red-100 text-red-800";
    default:
      return "bg-yellow-100 text-yellow-800";
  }
};

const Coupons = () => {
  const dispatch = useDispatch();
  const { coupons, status, error } = useSelector((state) => state.coupons);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");

  const handleOpenModal = (item = null) => {
    setCurrentCoupon(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentCoupon(null);
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteCoupons(deleteId)).unwrap();
      toast.success("Xóa sản phẩm thành công!");
      handleCloseDeleteModal();
    } catch (error) {
      toast.error("Đã xảy ra lỗi khi xóa mã thưởng!");
    }
  };

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchCoupons());
    }
  }, [status, dispatch]);

  // Đổi trạng thái duyệt
  const handleApproveStatusChange = async (id, approvedStatus) => {
    try {
      await ApiCustom.put(`/coupon-approve/${id}`, { approvedStatus });
      toast.success("Đã cập nhật trạng thái duyệt!");
      dispatch(fetchCoupons());
    } catch (err) {
      toast.error("Cập nhật trạng thái duyệt thất bại!");
    }
  };

  const headers = [
    {
      key: "code",
      label: "Mã thưởng",
      render: (item) => (
        <div>
          <p className="font-medium">{item.code}</p>
        </div>
      ),
    },
    {
      key: "price",
      label: "Giá tiền",
      render: (item) => (
        <div>
          <p className="font-medium">{formatCurrencyVND(item.price)}</p>
        </div>
      ),
    },
    {
      key: "userId",
      label: "Người dùng",
      render: (item) => (
        <div>
          <p className="font-medium">{item.user?.userFullName}</p>
        </div>
      ),
    },
    {
      key: "productId",
      label: "Sản phẩm",
      render: (item) => (
        <div>
          <p className="font-medium">
            {item.product ? item.product?.name : ""}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      label: "Trạng thái sử dụng",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            item.status === "active"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          } whitespace-nowrap`}
        >
          {item.status === "active" ? "Chưa dùng" : "Đã dùng"}
        </span>
      ),
    },
    // *** THÊM CỘT TRẠNG THÁI DUYỆT ***
    {
      key: "approvedStatus",
      label: "Trạng thái duyệt",
      render: (item) => (
        <StatusDropdown
          options={statusOptions}
          disabled={!hasPermission("COUPON_APPROVE")}
          value={item.approvedStatus || "pending"}
          onChange={(newStatus) =>
            handleApproveStatusChange(item.id, newStatus)
          }
          minWidth="w-32"
          getStatusStyle={getStatusStyle}
        />
      ),
    },
    {
      key: "startDate",
      label: "Ngày bắt đầu",
      render: (item) => (
        <div>
          <p className="font-medium">{formatDate(item.startDate)}</p>
        </div>
      ),
    },
    {
      key: "endDate",
      label: "Ngày kết thúc",
      render: (item) => (
        <div>
          <p className="font-medium">{formatDate(item.endDate)}</p>
        </div>
      ),
    },
  ];

  if (hasPermission("COUPON_UPDATE") || hasPermission("COUPON_DELETE")) {
    headers.push({ key: "setting", label: "Thao tác" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("COUPON_UPDATE") && (
          <button
            disabled={item.status == "success"}
            onClick={() => handleOpenModal(item)}
            title="Chỉnh sửa"
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
        )}
        {hasPermission("COUPON_DELETE") && (
          <button
            onClick={() => handleOpenDeleteModal(item.id)}
            title="Xóa"
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="p-4 text-red-500 bg-red-50 rounded-md">
        Đã xảy ra lỗi: {error}
      </div>
    );
  }

  const filterCompany = [
    ...new Set(
      coupons
        .map((item) => item.product?.company?.companyName)
        .filter((company) => company !== undefined && company !== null),
    ),
  ];

  const filterData = coupons.filter(
    (item) =>
      (searchValue === "" ||
        [item.code, item.user?.userFullName, item.product?.name].some((field) =>
          field?.toLowerCase().includes(searchValue.toLowerCase()),
        )) &&
      (selectedCompany === "" ||
        item.product?.company?.companyName === selectedCompany),
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold ">Quản lý mã thưởng cho hội viên</h1>
      </div>
      <div className="flex justify-between mb-4">
        {hasPermission("COUPON_CREATE") ? (
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-success transition-colors px-4 py-2 rounded mb-4"
          >
            + Thêm mã thưởng
          </button>
        ) : (
          <div></div>
        )}
        <div className="flex items-center gap-2">
          <div className=" flex items-center space-x-2">
            <label
              htmlFor="userSearchFilter"
              className="text-sm font-medium w-24"
            >
              Tìm kiếm:
            </label>
            <input
              type="search"
              className="w-full px-2 py-1 border rounded-lg focus:outline-none pr-10"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <select
            className="border px-2 py-1 rounded-md w-60"
            onChange={(e) => setSelectedCompany(e.target.value)}
          >
            <option value="">Chọn đơn vị</option>
            {filterCompany.map((name, index) => (
              <option key={index} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={filterData}
          settings={tableSettings}
        />
      </div>

      {isModalOpen && (
        <CouponModal coupon={currentCoupon} onClose={handleCloseModal} />
      )}

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Coupons;
